import React, { useState, useContext, useCallback } from "react";
import { Button } from "reactstrap";
import { useAuthToken } from "../../../../hooks/AuthHook.jsx";
import { getJournalByMovementID } from "../../../../api/index.js";
import { AdminStateContext } from "../../../../providers/AdminStateProvider.jsx";

import { useUuidField, useFormValidation } from "./hooks.js";
import { STYLES } from "./styles.js";
import {
  UuidInputField,
  ErrorState,
  InfoBox,
} from "./components.js";
import { JournalsTable, EmptyState } from "./JournalsTable.js";
import {
  generateCsvContent,
  downloadCsv,
  generateCsvFilename,
  handleError,
  validateResponseData,
} from "./utils.js";

const Journals = () => {
  // Estados principales
  const [journalData, setJournalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [error, setError] = useState(null);

  // Context y auth
  const authToken = useAuthToken();
  const [adminState] = useContext(AdminStateContext);
  const { country } = adminState;

  // Hooks personalizados para validación
  const movementField = useUuidField("", true); // opcional
  const batchField = useUuidField("", true); // opcional
  const isFormValid = useFormValidation(movementField, batchField);

  // Función para limpiar errores
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Función de búsqueda
  const handleBuscar = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isFormValid) return;

      setLoading(true);
      setError(null);

      try {
        const movementIdParam = movementField.isEmpty
          ? null
          : movementField.value;
        const batchIdParam = batchField.isEmpty ? null : batchField.value;

        const data = await getJournalByMovementID(
          country,
          authToken,
          movementIdParam,
          batchIdParam,
        );

        // Validar estructura de respuesta
        if (!validateResponseData(data)) {
          throw new Error("Formato de respuesta inválido del servidor");
        }

        setJournalData(data);
      } catch (error) {
        const errorMessage = handleError(
          error,
          "Error al obtener los datos del journal",
        );
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [isFormValid, movementField, batchField, country, authToken],
  );

  // Función de descarga CSV
  const handleDownloadCsv = useCallback(async () => {
    if (!journalData?.journals?.length) return;

    setDownloadingCsv(true);

    try {
      const csvContent = generateCsvContent(journalData.journals);
      const filename = generateCsvFilename(country);
      downloadCsv(csvContent, filename);
    } catch (error) {
      const errorMessage = handleError(
        error,
        "Error al generar el archivo CSV",
      );
      setError(errorMessage);
    } finally {
      setDownloadingCsv(false);
    }
  }, [journalData, country]);

  return (
    <div style={STYLES.container}>
      <h1>Asientos Contables (Journals)</h1>

      {/* Formulario de búsqueda */}
      <div style={STYLES.formContainer}>
        <form onSubmit={handleBuscar}>
          <UuidInputField
            id="movement-id-input"
            label="ID Movimiento"
            placeholder="Ingrese ID Movimiento"
            value={movementField.value}
            isValid={movementField.isValid}
            onChange={movementField.handleChange}
          />

          <UuidInputField
            id="batch-id-input"
            label="Batch ID"
            placeholder="Ingrese Batch ID"
            value={batchField.value}
            isValid={batchField.isValid}
            onChange={batchField.handleChange}
          />

          <InfoBox>
            <strong>Nota:</strong> Debe proporcionar al menos uno de los dos
            campos (ID Movimiento o Batch ID). Puede usar ambos para filtros más
            específicos.
          </InfoBox>

          <Button
            color="primary"
            type="submit"
            disabled={!isFormValid || loading}
            className="font-weight-bold"
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
        </form>
      </div>

      {/* Estados de carga y error */}
      {loading && (
        <div style={STYLES.loadingContainer}>
          <div className="lds-dual-ring"></div>
          <p>Cargando datos...</p>
        </div>
      )}

      {downloadingCsv && (
        <div style={STYLES.loadingContainer}>
          <div className="lds-dual-ring"></div>
          <p>Descargando archivo CSV...</p>
        </div>
      )}

      {error && <ErrorState error={error} onDismiss={clearError} />}

      {/* Resultados */}
      {journalData && (
        <div style={STYLES.resultsContainer}>
          <h2>Resultados del Journal</h2>

          {/* Resumen */}
          <div style={STYLES.summaryContainer}>
            <p>
              <strong>País:</strong> {journalData.country}
            </p>
            <p>
              <strong>Total de casos:</strong> {journalData.total}
            </p>
          </div>

          {/* Tabla o estado vacío */}
          {journalData.journals && journalData.journals.length > 0 ? (
            <JournalsTable
              journals={journalData.journals}
              onDownloadCsv={handleDownloadCsv}
              downloadingCsv={downloadingCsv}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      )}
    </div>
  );
};

export default Journals;
